import React, { useState, useRef, useEffect } from "react";
import { Modal, Form, Button, Spin, message, Tag, Typography } from "antd";
import VoiceRecorder from "./VoiceRecorder";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import AiMedicineAddForm from "./AiMedicineAddForm";
import { processAudioMedicationApi } from "../../../../services/medication.services";
import { medicationPrescribeAPI } from "../../../../services/patient.services";
import dayjs from "dayjs";
import useQuery from "../../../../hooks/useQuery";
import useIsMobile from "../../../../hooks/useIsMobile";
import { NURSE_IP_COMPACT_MODAL_WRAP } from "../../../IpEMR/Doctor/InitialInspection/ClosePopup";
import "../../../IpEMR/Doctor/InitialInspection/compactModal.css";
import "./RecordingModal.css";

const RecordingModal = ({
  open,
  setOpen,
  medicineData,
  startRecord,
  setStartRecord,
  refreshPage,
  setShowAddMedicine,
}) => {
  const [recordedFile, setRecordedFile] = useState(null);
  const [aiResponse, setAiResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formStep, setFormStep] = useState("record");
  const [tags, setTags] = useState(null);

  const formRef = useRef(null);
  const stopRecordingRef = useRef(null);
  const [form] = Form.useForm();
  const params = useQuery();
  const isMobile = useIsMobile();
  const [selectedFrequency, setSelectedFrequency] = useState(null);

  const filteredStartMedicine = medicineData
    ?.filter((item) => item?.status === "Started")
    ?.map((medicineName) => medicineName?.name);

  // Reset modal data when opened/closed
  useEffect(() => {
    if (open) {
      setRecordedFile(null);
      setAiResponse(null);
      setFormStep("record");
      setTags(null);
      form.resetFields();

      // Start recording after a brief delay
      setTimeout(() => {
        setStartRecord(true);
      }, 100);
    } else {
      setStartRecord(false);
      if (stopRecordingRef.current) {
        stopRecordingRef.current();
      }
    }
  }, [open]);

  const onSubmit = async () => {
    if (!recordedFile) {
      message.error("No recording found");
      return;
    }

    setStartRecord(false);
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("audio_file", recordedFile);
      fd.append("active_medicines", JSON.stringify(filteredStartMedicine));

      const response = await processAudioMedicationApi(fd);
      setFormStep("medicine");
      setAiResponse(response?.data || {});
      setTags(response?.data?.removed_medicines);
      message.success("Audio processed successfully!");
    } catch (error) {
      console.error(error);
      message.error("Failed to process audio.");
    } finally {
      setLoading(false);
    }
  };

  const onMedicineFormSubmit = async (values) => {
    const times = values?.schedule?.times.map((time) =>
      dayjs(time)?.format("HH")
    );
    try {
      setLoading(true);
      const finalData = {
        ...values,
        schedule: { times },
        removed_medicines: tags,
        ip_admission: Number(params?.patient),
      };
      await medicationPrescribeAPI(finalData);
      message.success("Medicine added successfully!");

      setStartRecord(false);
      if (stopRecordingRef.current) {
        stopRecordingRef.current();
      }

      setOpen(false);
      setShowAddMedicine(false);
      refreshPage();
    } catch (error) {
      console.error(error);
      message.error("Failed to add medicine.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (removedTag) => {
    const newTags = tags.filter((tag) => tag !== removedTag);
    setTags(newTags);
  };

  const handleCancel = () => {
    setStartRecord(false);
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      closeIcon={null}
      title={formStep === "record" ? "Recorder" : "Add Medicine"}
      width={isMobile ? "calc(100vw - 32px)" : "50%"}
      centered={isMobile}
      wrapClassName={
        isMobile
          ? `${NURSE_IP_COMPACT_MODAL_WRAP} medication-recorder-modal`
          : undefined
      }
      getContainer={() => document.body}
      maskClosable={false}
      keyboard={false}
      footer={
        <div className="flex-end" style={{ gap: isMobile ? "12px" : "24px" }}>
          <Form.Item>
            <Button
              type="primary"
              ghost
              className={btnStyles.mdBtn}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              htmlType="submit"
              type="primary"
              className={btnStyles.mdBtn}
              disabled={loading}
              onClick={() => {
                if (formStep === "record") {
                  onSubmit();
                } else if (formStep === "medicine" && formRef.current) {
                  formRef.current.submit();
                }
              }}
            >
              Submit
            </Button>
          </Form.Item>
        </div>
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" tip="Processing..." />
        </div>
      ) : (
        <Form
          layout="vertical"
          ref={formRef}
          form={form}
          onFinish={onMedicineFormSubmit}
        >
          {/* 🎙 Step 1: Voice Recorder */}
          {formStep === "record" && (
            <VoiceRecorder
              recordedFile={recordedFile}
              setRecordedFile={setRecordedFile}
              startRecord={startRecord}
              setStartRecord={setStartRecord}
              stopAllRecordings={stopRecordingRef}
            />
          )}

          {/* 💊 Step 2: AI Medicine Add Form */}
          {formStep === "medicine" && (
            <>
              <AiMedicineAddForm
                form={form}
                editData={aiResponse}
                selectedFrequency={selectedFrequency}
                setSelectedFrequency={setSelectedFrequency}
              />
              {tags?.length > 0 && (
                <div style={{ marginTop: "20px" }}>
                  <Typography.Text strong>
                    Medicines You've Stopped
                  </Typography.Text>
                  <div style={{ marginTop: "8px" }}>
                    {tags?.map((tag) => (
                      <Tag
                        key={tag}
                        closable
                        onClose={() => handleClose(tag)}
                        style={{ marginRight: "8px", marginBottom: "8px" }}
                        color="red"
                      >
                        {tag}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Form>
      )}
    </Modal>
  );
};

export default RecordingModal;
